import React, { createContext, useCallback } from "react";

type LocalStorageContextType = {
  getItem: (rootKey: string, nestedKey?: string) => any;
  setItem: (rootKey: string, value: any, nestedKey?: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
};

export const LocalStorageContext = createContext<LocalStorageContextType | undefined>(
  undefined
);

export const LocalStorageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const safeParse = (value: string | null) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch {
      return value;
    }
  };

  // 🔹 Get item with optional nested path
  const getItem = useCallback((rootKey: string, nestedKey?: string) => {
    const rootValue = safeParse(localStorage.getItem(rootKey));
    if (!nestedKey) return rootValue;

    return nestedKey.split(".").reduce((acc, k) => {
      if (acc && typeof acc === "object" && k in acc) {
        return acc[k];
      }
      return undefined;
    }, rootValue);
  }, []);

  // 🔹 Set item with optional nested path
  const setItem = useCallback((rootKey: string, value: any, nestedKey?: string) => {
    if (!nestedKey) {
      localStorage.setItem(rootKey, JSON.stringify(value));
      return;
    }

    const root = safeParse(localStorage.getItem(rootKey)) || {};
    let current: any = root;

    const keys = nestedKey.split(".");
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (!(k in current) || typeof current[k] !== "object") {
        current[k] = {};
      }
      current = current[k];
    }

    current[keys[keys.length - 1]] = value;

    localStorage.setItem(rootKey, JSON.stringify(root));
  }, []);

  const removeItem = useCallback((key: string) => {
    localStorage.removeItem(key);
  }, []);

  const clear = useCallback(() => {
    localStorage.clear();
  }, []);

  return (
    <LocalStorageContext.Provider value={{ getItem, setItem, removeItem, clear }}>
      {children}
    </LocalStorageContext.Provider>
  );
};
